// Google Maps cho bản đồ chọn địa chỉ: hiển thị bản đồ, tìm địa điểm (Places API New),
// tra địa chỉ từ tọa độ (Geocoding). Chạy trên trình duyệt bằng 1 key giới hạn theo tên miền;
// key lấy lúc chạy từ API (/geo/config). Không có key / tải lỗi / key bị từ chối → false,
// bản đồ quay về OpenStreetMap + /geo/* như cũ.

import { get } from '@/lib/api'

declare global {
  interface Window {
    __stGoogleMapsReady?: () => void
    /** Google gọi khi key sai / tên miền không được phép */
    gm_authFailure?: () => void
  }
}

let loading: Promise<boolean> | null = null
let authFailed = false
const authFailureListeners = new Set<() => void>()

/** Tải Google Maps JS một lần cho cả phiên. */
export function loadGoogleMaps(): Promise<boolean> {
  if (authFailed) return Promise.resolve(false)
  if (typeof (window as { google?: { maps?: { importLibrary?: unknown } } }).google?.maps?.importLibrary === 'function') return Promise.resolve(true)
  loading ??= (async () => {
    const cfg = await get<{ googleMapsKey: string | null }>('/geo/config').catch(() => null)
    if (!cfg?.googleMapsKey) return false
    window.gm_authFailure = () => {
      authFailed = true
      authFailureListeners.forEach((fn) => fn())
    }
    return new Promise<boolean>((resolve) => {
      const qs = new URLSearchParams({
        key: cfg.googleMapsKey!,
        v: 'weekly',
        language: 'vi',
        region: 'VN',
        loading: 'async',
        callback: '__stGoogleMapsReady',
      })
      const s = document.createElement('script')
      s.src = `https://maps.googleapis.com/maps/api/js?${qs}`
      s.async = true
      s.onerror = () => resolve(false)
      window.__stGoogleMapsReady = () => resolve(true)
      document.head.appendChild(s)
    })
  })()
  return loading
}

/** Đăng ký nghe lỗi key (gm_authFailure) — trả hàm hủy. */
export function onGoogleMapsAuthFailure(fn: () => void): () => void {
  authFailureListeners.add(fn)
  return () => authFailureListeners.delete(fn)
}

export const googleMapsAuthFailed = () => authFailed

// ─── Tra địa chỉ từ tọa độ ──────────────────────────────────────────

export interface RawGeoAddress {
  city: string
  district: string
  ward: string
  /** Số nhà + tên đường */
  address: string
  formattedAddress: string
}

const PRECISE = ['street_address', 'premise', 'subpremise', 'establishment', 'point_of_interest']

export async function googleReverseGeocode(lat: number, lng: number): Promise<RawGeoAddress | null> {
  const { Geocoder } = (await google.maps.importLibrary('geocoding')) as google.maps.GeocodingLibrary
  const { results } = await new Geocoder().geocode({ location: { lat, lng } })
  if (!results.length) return null
  const best =
    results.find((r) => r.types.some((t) => PRECISE.includes(t))) ?? results.find((r) => r.types.includes('route')) ?? results[0]!
  // Cấp hành chính: lấy từ kết quả tốt nhất, thiếu thì từ các kết quả rộng hơn
  const pick = (type: string, from = [best, ...results]) => {
    for (const r of from) {
      const c = r.address_components.find((x) => x.types.includes(type))
      if (c) return c.long_name
    }
    return ''
  }
  const street = [pick('street_number', [best]), pick('route', [best])].filter(Boolean).join(' ')
  return {
    city: pick('administrative_area_level_1'),
    district: pick('administrative_area_level_2'),
    ward: pick('administrative_area_level_3') || pick('sublocality_level_1') || pick('sublocality') || pick('neighborhood'),
    address: street,
    formattedAddress: best.formatted_address.replace(/,\s*(Việt Nam|Vietnam)$/i, ''),
  }
}

// ─── Tìm địa điểm (gợi ý khi gõ) ────────────────────────────────────

export interface GoogleSuggestion {
  id: string
  name: string
  address: string
  prediction: google.maps.places.PlacePrediction
}

// Một phiên = các lần gõ + 1 lần lấy chi tiết → Google tính như một lượt tìm
let session: google.maps.places.AutocompleteSessionToken | null = null

export async function googleAutocomplete(input: string, near?: { lat: number; lng: number }): Promise<GoogleSuggestion[]> {
  const { AutocompleteSuggestion, AutocompleteSessionToken } = (await google.maps.importLibrary('places')) as google.maps.PlacesLibrary
  session ??= new AutocompleteSessionToken()
  const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
    input,
    sessionToken: session,
    includedRegionCodes: ['vn'],
    language: 'vi',
    region: 'vn',
    ...(near ? { origin: near, locationBias: { center: near, radius: 30_000 } } : {}),
  })
  return suggestions.flatMap((s) => {
    const p = s.placePrediction
    if (!p) return []
    return [{ id: p.placeId, name: p.mainText?.text ?? p.text.text, address: p.secondaryText?.text ?? p.text.text, prediction: p }]
  })
}

/** Tọa độ của gợi ý đã chọn (kết thúc phiên tìm). */
export async function googleSuggestionLocation(s: GoogleSuggestion): Promise<{ lat: number; lng: number } | null> {
  const place = s.prediction.toPlace()
  await place.fetchFields({ fields: ['location'] })
  session = null
  return place.location ? { lat: place.location.lat(), lng: place.location.lng() } : null
}
